import { useState, useEffect, useRef } from "react";
import emailjs from "@emailjs/browser";

const EMAILJS_SERVICE_ID = "service_qd9ir6b";
const EMAILJS_TEMPLATE_ID = "template_pkynoe8";
const EMAILJS_PUBLIC_KEY = "lCVyIuTBzJPuy-cLc";

const NAV_LINKS = ["Home", "About", "Services", "Portfolio", "Contact"];

const TECH_STACK = {
  "Relational Databases": ["Oracle", "MS SQL Server", "MySQL", "PostgreSQL"],
  "NoSQL & Document Databases": ["MongoDB", "Redis", "DynamoDB", "CouchDB", "Apache Cassandra", "HBase"],
  "In-Memory & Real-Time": ["Oracle TimesTen"],
  "Backend": ["Java (Native)", "Spring Boot", "Spring Cloud", "Java EE / EJB", "JBoss EAP"],
  "Frontend": ["Angular", "React", "Vue.js", "React Native"],
  "Telecom & Network": ["2G/3G/4G/5G Platforms", "SMPP / SMS Center", "Telnet", "OpenDaylight", "SNMP Protocol"],
};

const TECH_ICONS = {
  "Relational Databases": "🗄️",
  "NoSQL & Document Databases": "📦",
  "In-Memory & Real-Time": "⚡",
  "Backend": "⚙️",
  "Frontend": "🖥️",
  "Telecom & Network": "📡",
};

const PORTFOLIO = [
  { title: "Enterprise Banking Platform", desc: "A large-scale multi-tier banking system handling millions of transactions daily, built with Java Spring Boot, Oracle DB, and Angular frontend.", tags: ["Java", "Spring Boot", "Oracle", "Angular"] },
  { title: "Telecom Subscriber Provisioning", desc: "End-to-end subscriber lifecycle management for a national telecom operator, integrating with 4G/5G IMS infrastructure and SMS center platforms.", tags: ["Java", "5G/IMS", "SMPP", "PostgreSQL"] },
  { title: "Real-Time Analytics Dashboard", desc: "High-performance analytics platform using in-memory databases and reactive frontend, delivering real-time insights across distributed microservices.", tags: ["Spring Cloud", "Redis", "Oracle TimesTen", "React"] },
  { title: "Cross-Platform Mobile App", desc: "A cross-platform mobile application for enterprise resource management, deployed on both iOS and Android using React Native.", tags: ["React Native", "MongoDB", "iOS/Android"] },
];

const scrollTo = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
};

function FadeIn({ children, delay = 0, direction = "up", style = {} }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setTimeout(() => setVisible(true), delay);
        obs.disconnect();
      }
    }, { threshold: 0.1, rootMargin: "0px 0px -50px 0px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay]);

  const transforms = {
    up: "translateY(40px)",
    left: "translateX(-50px)",
    right: "translateX(50px)",
    none: "none"
  };

  return (
      <div ref={ref} style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translate(0,0)" : transforms[direction] || transforms.up,
        transition: `opacity 0.6s ease, transform 0.6s ease`,
        ...style
      }}>
        {children}
      </div>
  );
}

const inp = { padding: "14px 18px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.2)", background: "rgba(255,255,255,0.1)", color: "#fff", fontSize: 15, outline: "none", width: "100%", boxSizing: "border-box" };

// Hamburger icon
function Hamburger({ open, color }) {
  return (
      <div style={{ width: 24, height: 18, display: "flex", flexDirection: "column", justifyContent: "space-between", cursor: "pointer" }}>
        <span style={{ display: "block", height: 2, background: color, borderRadius: 2, transition: "all 0.3s", transform: open ? "rotate(45deg) translate(5px, 8px)" : "none" }} />
        <span style={{ display: "block", height: 2, background: color, borderRadius: 2, transition: "all 0.3s", opacity: open ? 0 : 1 }} />
        <span style={{ display: "block", height: 2, background: color, borderRadius: 2, transition: "all 0.3s", transform: open ? "rotate(-45deg) translate(5px, -8px)" : "none" }} />
      </div>
  );
}

export default function App() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const handleSend = async () => {
    if (!form.name || !form.email || !form.message) return;
    setSending(true);
    setError("");
    try {
      await emailjs.send(
          EMAILJS_SERVICE_ID,
          EMAILJS_TEMPLATE_ID,
          { from_name: form.name, email: form.email, message: form.message },
          EMAILJS_PUBLIC_KEY
      );
      setSent(true);
    } catch (err) {
      setError("Something went wrong. Please try again or email us directly at info@tskaro.io");
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("scroll", onScroll);
    window.addEventListener("resize", onResize);
    return () => { window.removeEventListener("scroll", onScroll); window.removeEventListener("resize", onResize); };
  }, []);

  // Stars animation
  useEffect(() => {
    const canvas = document.getElementById("starsCanvas");
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let W = canvas.width = window.innerWidth;
    let H = canvas.height = window.innerHeight;
    let animId;

    const stars = Array.from({ length: 160 }, () => ({
      x: Math.random() * W,
      y: Math.random() * H,
      r: Math.random() * 1.8 + 0.3,
      dx: (Math.random() - 0.5) * 0.3,
      dy: (Math.random() - 0.5) * 0.3,
      opacity: Math.random() * 0.6 + 0.2,
      twinkle: Math.random() * Math.PI * 2,
    }));

    // Draw connecting lines between nearby stars
    const drawLines = () => {
      for (let i = 0; i < stars.length; i++) {
        for (let j = i + 1; j < stars.length; j++) {
          const dx = stars[i].x - stars[j].x;
          const dy = stars[i].y - stars[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            ctx.beginPath();
            ctx.moveTo(stars[i].x, stars[i].y);
            ctx.lineTo(stars[j].x, stars[j].y);
            ctx.strokeStyle = `rgba(147, 197, 253, ${(1 - dist / 120) * 0.15})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, W, H);
      stars.forEach(s => {
        s.x += s.dx;
        s.y += s.dy;
        s.twinkle += 0.02;
        const op = s.opacity + Math.sin(s.twinkle) * 0.2;
        if (s.x < 0) s.x = W;
        if (s.x > W) s.x = 0;
        if (s.y < 0) s.y = H;
        if (s.y > H) s.y = 0;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(200, 225, 255, ${op})`;
        ctx.fill();
      });
      drawLines();
      animId = requestAnimationFrame(animate);
    };

    animate();

    const onResize2 = () => {
      W = canvas.width = window.innerWidth;
      H = canvas.height = window.innerHeight;
    };
    window.addEventListener("resize", onResize2);
    return () => { cancelAnimationFrame(animId); window.removeEventListener("resize", onResize2); };
  }, []);

  const handleNav = (id) => { scrollTo(id); setMenuOpen(false); };

  return (
      <div style={{ fontFamily: "'Segoe UI', sans-serif", color: "#1a1a2e", background: "#f9fafb", overflowX: "hidden" }}>

        {/* NAVBAR */}
        <nav style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
          background: scrolled || menuOpen ? "rgba(255,255,255,0.97)" : "rgba(255,255,255,0.05)",
          borderBottom: scrolled || menuOpen ? "1px solid #e2e8f0" : "1px solid transparent",
          boxShadow: scrolled ? "0 2px 12px rgba(0,0,0,0.06)" : "none",
          backdropFilter: "blur(10px)",
          transition: "all 0.3s ease"
        }}>
          <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 24px", display: "flex", alignItems: "center", justifyContent: "space-between", height: 64 }}>
            {/* Logo text */}
            <div style={{ fontSize: 22, fontWeight: 800, color: scrolled || menuOpen ? "#1e40af" : "#fff", letterSpacing: "-0.5px", cursor: "pointer", transition: "color 0.3s", flexShrink: 0 }} onClick={() => handleNav("home")}>
              ts<span style={{ color: scrolled || menuOpen ? "#3b82f6" : "#93c5fd" }}>karo</span><span style={{ color: "#94a3b8", fontSize: 14 }}>.io</span>
            </div>

            {/* Desktop nav */}
            {!isMobile && (
                <div style={{ display: "flex", gap: 28 }}>
                  {NAV_LINKS.map(l => (
                      <button key={l} onClick={() => handleNav(l.toLowerCase())}
                              style={{ background: "none", border: "none", cursor: "pointer", fontSize: 15, fontWeight: 500, color: scrolled ? "#374151" : "#fff", transition: "color 0.3s" }}>
                        {l}
                      </button>
                  ))}
                </div>
            )}

            {/* Desktop CTA / Mobile Hamburger */}
            {!isMobile ? (
                <button onClick={() => handleNav("contact")}
                        style={{ background: scrolled ? "#1e40af" : "rgba(255,255,255,0.2)", color: "#fff", border: scrolled ? "none" : "1px solid rgba(255,255,255,0.4)", borderRadius: 8, padding: "10px 22px", fontWeight: 600, cursor: "pointer", fontSize: 14, transition: "all 0.3s", flexShrink: 0 }}>
                  Get In Touch
                </button>
            ) : (
                <div onClick={() => setMenuOpen(o => !o)}>
                  <Hamburger open={menuOpen} color={scrolled || menuOpen ? "#1e40af" : "#fff"} />
                </div>
            )}
          </div>

          {/* Mobile dropdown menu */}
          {isMobile && (
              <div style={{
                maxHeight: menuOpen ? 400 : 0, overflow: "hidden",
                transition: "max-height 0.4s ease",
                background: "rgba(255,255,255,0.97)",
                borderTop: menuOpen ? "1px solid #e2e8f0" : "none"
              }}>
                <div style={{ padding: "12px 24px 20px", display: "flex", flexDirection: "column", gap: 4 }}>
                  {NAV_LINKS.map(l => (
                      <button key={l} onClick={() => handleNav(l.toLowerCase())}
                              style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, fontWeight: 500, color: "#374151", padding: "10px 0", textAlign: "left", borderBottom: "1px solid #f1f5f9" }}>
                        {l}
                      </button>
                  ))}
                  <button onClick={() => handleNav("contact")}
                          style={{ background: "#1e40af", color: "#fff", border: "none", borderRadius: 8, padding: "12px", fontWeight: 600, cursor: "pointer", fontSize: 15, marginTop: 8 }}>
                    Get In Touch
                  </button>
                </div>
              </div>
          )}
        </nav>

        {/* HERO */}
        <section id="home" style={{ minHeight: "100vh", display: "flex", alignItems: "center", background: "linear-gradient(135deg, #0f1f5c 0%, #1a3a9f 40%, #1d4ed8 100%)", padding: "100px 24px 80px", boxSizing: "border-box", position: "relative", overflow: "hidden" }}>
          {/* Stars Canvas inside hero */}
          <canvas id="starsCanvas" style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", zIndex: 0, pointerEvents: "none" }} />
          <div style={{ position: "relative", zIndex: 1, maxWidth: 1100, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 60, flexWrap: "wrap", width: "100%" }}>
            <FadeIn direction="left" delay={100} style={{ flex: "1 1 300px", minWidth: 0 }}>
              <div style={{ display: "inline-block", background: "rgba(255,255,255,0.12)", color: "#bfdbfe", borderRadius: 20, padding: "6px 18px", fontSize: 13, fontWeight: 600, marginBottom: 24, letterSpacing: 1 }}>
                ENTERPRISE SOFTWARE DEVELOPMENT
              </div>
              <h1 style={{ fontSize: "clamp(28px, 5vw, 58px)", fontWeight: 900, color: "#fff", lineHeight: 1.15, margin: "0 0 24px" }}>
                Developing Enterprise<br /><span style={{ color: "#93c5fd" }}>Systems is Easy.</span>
              </h1>
              <p style={{ fontSize: 17, color: "#bfdbfe", lineHeight: 1.7, marginBottom: 40, maxWidth: 500 }}>
                We design and build scalable, high-performance enterprise systems — from database architecture to mobile applications — tailored for the most demanding industries.
              </p>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                <button onClick={() => scrollTo("portfolio")} style={{ background: "#fff", color: "#1e40af", border: "none", borderRadius: 10, padding: "14px 28px", fontWeight: 700, cursor: "pointer", fontSize: 15 }}>
                  View Our Work
                </button>
                <button onClick={() => scrollTo("contact")} style={{ background: "transparent", color: "#fff", border: "2px solid rgba(255,255,255,0.5)", borderRadius: 10, padding: "14px 28px", fontWeight: 700, cursor: "pointer", fontSize: 15 }}>
                  Contact Us
                </button>
              </div>
            </FadeIn>
            <FadeIn direction="right" delay={300} style={{ flex: "1 1 260px", display: "flex", alignItems: "center", justifyContent: "center", minWidth: 0 }}>
              <img src="/headerLogo.png" alt="Tskaro Logo"
                   style={{ width: "100%", maxWidth: 480, filter: "drop-shadow(0 0 40px rgba(147,197,253,0.6)) drop-shadow(0 20px 60px rgba(0,0,0,0.5))", animation: "float 4s ease-in-out infinite" }} />
            </FadeIn>
          </div>
        </section>

        <style>{`
        @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-16px)} }
        * { box-sizing: border-box; }
      `}</style>

        {/* ABOUT */}
        <section id="about" style={{ padding: "80px 24px", background: "#fff" }}>
          <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", gap: 60, alignItems: "center", flexWrap: "wrap" }}>
            <FadeIn direction="left" style={{ flex: "1 1 280px", minWidth: 0 }}>
              <div style={{ color: "#3b82f6", fontWeight: 700, fontSize: 13, letterSpacing: 2, marginBottom: 12 }}>ABOUT US</div>
              <h2 style={{ fontSize: "clamp(26px, 4vw, 36px)", fontWeight: 800, marginBottom: 20, lineHeight: 1.2 }}>We Build Systems<br />That Scale.</h2>
              <p style={{ color: "#4b5563", lineHeight: 1.8, fontSize: 16, marginBottom: 16 }}>
                Founded in 2018, <strong>Tskaro</strong> is a software engineering company specializing in large-scale enterprise systems. Based in Tbilisi, Georgia, our team of 25+ engineers delivers end-to-end solutions across backend, frontend, mobile, and telecommunications domains.
              </p>
              <p style={{ color: "#4b5563", lineHeight: 1.8, fontSize: 16 }}>
                Our mission is simple: make complex enterprise software development straightforward, reliable, and future-proof.
              </p>
            </FadeIn>
            <FadeIn direction="right" delay={200} style={{ flex: "1 1 260px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, minWidth: 0 }}>
              {[["25+", "Engineers"], ["50+", "Projects Delivered"], ["7+", "Years Experience"], ["15+", "Enterprise Clients"]].map(([num, label], i) => (
                  <FadeIn key={label} delay={i * 100} direction="up">
                    <div style={{ background: "#f0f7ff", borderRadius: 16, padding: "24px 16px", textAlign: "center" }}>
                      <div style={{ fontSize: 34, fontWeight: 900, color: "#1e40af" }}>{num}</div>
                      <div style={{ color: "#64748b", fontWeight: 500, fontSize: 13, marginTop: 4 }}>{label}</div>
                    </div>
                  </FadeIn>
              ))}
            </FadeIn>
          </div>
        </section>

        {/* SERVICES */}
        <section id="services" style={{ padding: "80px 24px", background: "#f8fafc" }}>
          <div style={{ maxWidth: 1100, margin: "0 auto" }}>
            <FadeIn direction="up" style={{ textAlign: "center", marginBottom: 60 }}>
              <div style={{ color: "#3b82f6", fontWeight: 700, fontSize: 13, letterSpacing: 2, marginBottom: 12 }}>WHAT WE USE</div>
              <h2 style={{ fontSize: "clamp(26px, 4vw, 36px)", fontWeight: 800, margin: 0 }}>Our Technology Stack</h2>
              <p style={{ color: "#6b7280", marginTop: 12, fontSize: 16 }}>Deep expertise across the full spectrum of enterprise technologies</p>
            </FadeIn>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20, alignItems: "stretch" }}>
              {Object.entries(TECH_STACK).map(([cat, techs], i) => (
                  <FadeIn key={cat} delay={i * 80} direction="up" style={{ minWidth: 0, height: "100%" }}>
                    <div style={{ background: "#fff", borderRadius: 16, padding: 24, boxShadow: "0 2px 12px rgba(0,0,0,0.06)", border: "1px solid #e2e8f0", height: "100%" }}>
                      <div style={{ fontSize: 26, marginBottom: 10 }}>{TECH_ICONS[cat]}</div>
                      <h3 style={{ fontSize: 15, fontWeight: 700, color: "#1e40af", marginBottom: 12 }}>{cat}</h3>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                        {techs.map(t => (
                            <span key={t} style={{ background: "#eff6ff", color: "#1d4ed8", borderRadius: 6, padding: "4px 10px", fontSize: 12, fontWeight: 500 }}>{t}</span>
                        ))}
                      </div>
                    </div>
                  </FadeIn>
              ))}
            </div>
          </div>
        </section>

        {/* PORTFOLIO */}
        <section id="portfolio" style={{ padding: "80px 24px", background: "#fff" }}>
          <div style={{ maxWidth: 1100, margin: "0 auto" }}>
            <FadeIn direction="up" style={{ textAlign: "center", marginBottom: 60 }}>
              <div style={{ color: "#3b82f6", fontWeight: 700, fontSize: 13, letterSpacing: 2, marginBottom: 12 }}>OUR WORK</div>
              <h2 style={{ fontSize: "clamp(26px, 4vw, 36px)", fontWeight: 800, margin: 0 }}>Featured Projects</h2>
              <p style={{ color: "#6b7280", marginTop: 12, fontSize: 16 }}>A selection of enterprise solutions we've delivered</p>
            </FadeIn>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 24 }}>
              {PORTFOLIO.map((p, i) => (
                  <FadeIn key={i} delay={i * 100} direction="up">
                    <div style={{ background: "#f8fafc", borderRadius: 16, padding: 28, border: "1px solid #e2e8f0", height: "100%" }}>
                      <div style={{ width: 44, height: 44, background: "linear-gradient(135deg, #1e40af, #3b82f6)", borderRadius: 12, marginBottom: 18 }} />
                      <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 10 }}>{p.title}</h3>
                      <p style={{ color: "#4b5563", lineHeight: 1.7, fontSize: 14, marginBottom: 16 }}>{p.desc}</p>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                        {p.tags.map(t => (
                            <span key={t} style={{ background: "#dbeafe", color: "#1e40af", borderRadius: 6, padding: "4px 10px", fontSize: 12, fontWeight: 600 }}>{t}</span>
                        ))}
                      </div>
                    </div>
                  </FadeIn>
              ))}
            </div>
          </div>
        </section>

        {/* CONTACT */}
        <section id="contact" style={{ padding: "80px 24px", background: "linear-gradient(135deg, #1e3a8a, #1e40af)" }}>
          <div style={{ maxWidth: 680, margin: "0 auto", textAlign: "center" }}>
            <FadeIn direction="up">
              <div style={{ color: "#93c5fd", fontWeight: 700, fontSize: 13, letterSpacing: 2, marginBottom: 12 }}>GET IN TOUCH</div>
              <h2 style={{ fontSize: "clamp(26px, 4vw, 36px)", fontWeight: 800, color: "#fff", marginBottom: 12 }}>Let's Build Something Great</h2>
              <p style={{ color: "#bfdbfe", fontSize: 16, marginBottom: 40 }}>Have a project in mind? We'd love to hear about it.</p>
              <div style={{ display: "flex", gap: 24, justifyContent: "center", flexWrap: "wrap", marginBottom: 40 }}>
                {[["📧", "hello@tskaro.io"], ["📍", "Tbilisi, Georgia"], ["📞", "+995 599 69 39 39"]].map(([icon, val]) => (
                    <div key={val} style={{ color: "#bfdbfe", fontSize: 14 }}>{icon} {val}</div>
                ))}
              </div>
              {sent ? (
                  <div style={{ background: "rgba(255,255,255,0.12)", borderRadius: 16, padding: 48, color: "#fff" }}>
                    <div style={{ fontSize: 52, marginBottom: 16 }}>✅</div>
                    <h3 style={{ fontSize: 22, fontWeight: 700, margin: "0 0 8px" }}>Message Sent!</h3>
                    <p style={{ color: "#bfdbfe", margin: 0 }}>We'll get back to you within 24 hours.</p>
                  </div>
              ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 14, textAlign: "left" }}>
                    <input type="text" placeholder="Your Name" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} style={inp} />
                    <input type="email" placeholder="Your Email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} style={inp} />
                    <textarea placeholder="Tell us about your project..." rows={5} value={form.message} onChange={e => setForm(f => ({...f, message: e.target.value}))} style={{ ...inp, resize: "vertical" }} />
                    {error && <p style={{ color: "#fca5a5", fontSize: 14, margin: 0 }}>{error}</p>}
                    <button onClick={handleSend} disabled={sending}
                            style={{ background: sending ? "rgba(255,255,255,0.5)" : "#fff", color: "#1e40af", border: "none", borderRadius: 10, padding: 16, fontWeight: 700, fontSize: 16, cursor: sending ? "not-allowed" : "pointer" }}>
                      {sending ? "Sending..." : "Send Message →"}
                    </button>
                  </div>
              )}
            </FadeIn>
          </div>
        </section>

        {/* FOOTER */}
        <footer style={{ background: "#0f172a", color: "#64748b", textAlign: "center", padding: "24px", fontSize: 14 }}>
          © 2025 Tskaro. All rights reserved. | tskaro.io
        </footer>
      </div>
  );
}
